import { lazy, type ReactElement } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';

import { RequireCapability, RequireFlag, type FlagId } from '@core/access';
import { LandingRedirect } from '@app/LandingRedirect';
import { RemountOn } from '@shared/components/remount-on/RemountOn';
import { settingsRoutes } from '@features/settings/routes';

import { valuationRoutes } from '@features/valuations/routes';
import { crmRoutes } from '@features/crm/routes';
import {
  LegacyValuationRedirect,
  LegacyWorkScreenRedirect
} from '@features/valuations/legacy-redirects';

/**
 * Top level routing table.
 *
 * Every screen is code split with `React.lazy`, so the initial bundle carries
 * only the shell (toolbar, sidebar, assistant) and each route pulls its own
 * chunk on first visit.
 *
 * Two ordering rules matter:
 *  - the specific `activity-details/*` routes are declared before the
 *    catch-all `:id`, so the more precise URL always wins;
 *  - the `*` fallback stays last.
 *
 * No route declares its own auth guard: `<RequireAuth>` wraps the whole shell
 * in main.tsx, which covers every route below at once.
 *
 * Feature flags: a subtree behind a capability guard (Assignments, CRM) needs
 * nothing extra, because a switched-off flag removes the capability. The
 * surfaces below with no capability guard are wrapped in `flagged(...)`
 * instead, so turning one off also closes it to someone typing the URL.
 */

/** The element, while `flag` is on; 404 while it is off. */
function flagged(flag: FlagId, element: ReactElement = <Outlet />): ReactElement {
  return <RequireFlag flag={flag}>{element}</RequireFlag>;
}

/* ------------------------------------------------------------------- home -- */
const HomePage = lazy(() => import('@features/home/HomePage'));
const SearchPage = lazy(() => import('@features/search/SearchPage'));
const ArgusPowerBiPage = lazy(() => import('@features/argus/ArgusPowerBiPage'));

/* ------------------------------------------------------------ report studio -- */
const TemplateBuilderPage = lazy(() => import('@features/report-studio/pages/TemplateBuilderPage'));
const ReportWriterPage = lazy(() => import('@features/report-studio/pages/ReportWriterPage'));

/* ------------------------------------------------------- agent marketplace -- */
const AgentMarketplacePage = lazy(() => import('@features/agent-marketplace/pages/AgentMarketplacePage'));
const AgentDetailPage = lazy(() => import('@features/agent-marketplace/pages/AgentDetailPage'));
const AgentBuilderPage = lazy(() => import('@features/agent-marketplace/pages/AgentBuilderPage'));

/* ---------------------------------------------------------------------- map -- */
const MapWorkspacePage = lazy(() => import('@features/map/MapWorkspacePage'));

/* ---------------------------------------------------------------- appraisal -- */
const AppraisalTablePage = lazy(() => import('@features/appraisal/appraisal-table/AppraisalTablePage'));
const CloserContainerPage = lazy(() => import('@features/appraisal/closer-container/CloserContainerPage'));
const CommentsContainerPage = lazy(() => import('@features/appraisal/comments-container/CommentsContainerPage'));
const SiteInformationPage = lazy(() => import('@features/appraisal/site-information/SiteInformationPage'));
const ImprovementInformationPage = lazy(
  () => import('@features/appraisal/improvement-information/ImprovementInformationPage')
);
const ScopeOfWorkPage = lazy(() => import('@features/appraisal/scope-of-work/ScopeOfWorkPage'));
const AppraisalItemPage = lazy(() => import('@features/appraisal/appraisal-item/AppraisalItemPage'));
const ChangeHistoryPage = lazy(() => import('@features/appraisal/change-history/ChangeHistoryPage'));

/* ------------------------------------------------------------------- argus -- */
const ArgusPage = lazy(() => import('@features/argus/ArgusPage'));

/* ------------------------------------------------------------- comparables -- */
const ComparablesPage = lazy(() => import('@features/comparables/ComparablesPage'));
const ComparableSalesPage = lazy(() => import('@features/comparables/comparable-sales/ComparableSalesPage'));
const ComparableRentsPage = lazy(() => import('@features/comparables/comparable-rents/ComparableRentsPage'));
const ComparableExpensePage = lazy(() => import('@features/comparables/comparable-expense/ComparableExpensePage'));
const ComparableLandPage = lazy(() => import('@features/comparables/comparable-land/ComparableLandPage'));

/* ----------------------------------------------------------------- ratings -- */
const LiabilityPage = lazy(() => import('@features/ratings/liability/LiabilityPage'));
const AllowancePage = lazy(() => import('@features/ratings/allowance/AllowancePage'));
const AllowanceStatusPage = lazy(() => import('@features/ratings/allowance-status/AllowanceStatusPage'));
const AppealSavingsPage = lazy(() => import('@features/ratings/appeal-savings/AppealSavingsPage'));
const AppealStatusPage = lazy(() => import('@features/ratings/appeal-status/AppealStatusPage'));
const PropertySearchPage = lazy(() => import('@features/ratings/property-search/PropertySearchPage'));

/* --------------------------------------------------------- standalone tools -- */
const VaultPage = lazy(() => import('@features/vault/VaultPage'));
const PromptsPage = lazy(() => import('@features/assistant/prompts/PromptsPage'));
const FaqPage = lazy(() => import('@features/faq/FaqPage'));
const TestPage = lazy(() => import('@features/test/TestPage'));
const LoginPage = lazy(() => import('@features/auth/login/LoginPage'));
const NotFoundPage = lazy(() => import('@features/not-found/NotFoundPage'));

export const routes: RouteObject[] = [
  // The role's landing page from Settings > Preferences; Home by default.
  { index: true, element: <LandingRedirect /> },

  // Home
  { path: 'home/argus', element: flagged('connectors', <ArgusPowerBiPage />) },
  { path: 'home', element: <HomePage /> },

  // Search across the portal's domains, with AI Mode as one of its tabs.
  { path: 'search', element: <SearchPage /> },

  /*
   * Assignments - the valuation record and the work around it.
   *
   * The module owns its subtree and its own capability gate, so it contributes
   * a route list rather than individual entries. Everything that used to live
   * under `home/my-assignments` and under `sg/ivs` is inside it.
   */
  ...valuationRoutes,

  // CRM - companies, contacts and people, and the opportunities tagged with them.
  ...crmRoutes,

  // The addresses those two features used to have. Kept so that bookmarks and
  // links already sent to clients still arrive somewhere true.
  {
    path: 'home/my-assignments',
    children: [
      { index: true, element: <Navigate to="/assignments" replace /> },
      { path: 'tasks', element: <LegacyWorkScreenRedirect tab="tasks" /> },
      { path: 'files', element: <LegacyWorkScreenRedirect tab="files" /> },
      { path: 'inspection', element: <LegacyWorkScreenRedirect tab="inspection" /> },
      { path: 'comments', element: <LegacyWorkScreenRedirect tab="comments" /> }
    ]
  },
  {
    path: 'sg/ivs',
    children: [
      { index: true, element: <LegacyValuationRedirect /> },
      { path: 'new', element: <Navigate to="/assignments/new" replace /> },
      { path: 'admin/master-data', element: <Navigate to="/assignments/admin/master-data" replace /> },
      { path: ':ivid', element: <LegacyValuationRedirect /> }
    ]
  },

  // Report Studio. Two surfaces (template authoring, report writing) over one
  // binding model, so they share a route file and a state store.
  {
    path: 'report-studio',
    element: flagged('reportStudio'),
    children: [
      { index: true, element: <Navigate to="/report-studio/writer" replace /> },
      { path: 'builder', element: flagged('templateBuilder', <TemplateBuilderPage />) },
      { path: 'writer', element: <ReportWriterPage /> }
    ]
  },

  // Agent marketplace. `build` is declared before the catch-all `:agentId`, so
  // /agents/build is the builder rather than a listing for an agent called
  // "build" - the same ordering rule as activity-details below.
  {
    path: 'agents',
    element: flagged('agents'),
    children: [
      { index: true, element: <AgentMarketplacePage /> },
      { path: 'build', element: flagged('agentBuilder', <AgentBuilderPage />) },
      { path: 'build/:agentId', element: flagged('agentBuilder', <AgentBuilderPage />) },
      { path: ':agentId', element: <RemountOn param="agentId"><AgentDetailPage /></RemountOn> }
    ]
  },

  // Map workspace. One route: the whole map state lives in query params, so a
  // saved view and a share link are the same URL with different params.
  { path: 'map', element: flagged('map', <MapWorkspacePage />) },

  // Appraisal
  { path: 'grid', element: <AppraisalTablePage /> },
  { path: 'closer', element: <CloserContainerPage /> },
  { path: 'comments', element: <CommentsContainerPage /> },
  { path: 'site', element: <SiteInformationPage /> },
  { path: 'improvement', element: <ImprovementInformationPage /> },
  { path: 'scopeOfWork', element: <ScopeOfWorkPage /> },

  // Activity detail. The specific children are declared before the catch-all
  // `:id` route so the more precise URL always wins.
  { path: 'activity-details/argus/:id', element: <RemountOn param="id"><ArgusPage /></RemountOn> },
  { path: 'activity-details/changeHistory/:id', element: <RemountOn param="id"><ChangeHistoryPage /></RemountOn> },
  {
    path: 'activity-details/comparables/:id',
    element: <RemountOn param="id"><ComparablesPage /></RemountOn>,
    children: [
      { path: 'sales', element: <ComparableSalesPage /> },
      { path: 'rent', element: <ComparableRentsPage /> },
      { path: 'expense', element: <ComparableExpensePage /> },
      { path: 'land', element: <ComparableLandPage /> }
    ]
  },
  { path: 'activity-details/:id', element: <RemountOn param="id"><AppraisalItemPage /></RemountOn> },

  // Ratings
  {
    path: 'ratings',
    element: flagged('ratings'),
    children: [
      { path: 'liability', element: <LiabilityPage /> },
      { path: 'allowance', element: <AllowancePage /> },
      { path: 'allowanceStatus', element: <AllowanceStatusPage /> },
      { path: 'appealSavings', element: <AppealSavingsPage /> },
      { path: 'appealStatus', element: <AppealStatusPage /> },
      { path: 'propertySearch', element: <PropertySearchPage /> }
    ]
  },

  // Standalone tools
  { path: 'vault', element: flagged('vault', <VaultPage />) },
  { path: 'prompt', element: flagged('promptLibrary', <PromptsPage />) },
  { path: 'faq', element: flagged('faq', <FaqPage />) },

  // Settings - scoped flags, formats, role defaults and rules. Never behind a
  // flag: the switchboard cannot be allowed to switch itself off.
  {
    path: 'settings',
    element: (
      <RequireCapability capability="platform.settings">
        <Outlet />
      </RequireCapability>
    ),
    children: settingsRoutes
  },
  // Where the feature flags page used to live.
  { path: 'admin/feature-flags', element: <Navigate to="/settings/feature-flags" replace /> },
  { path: 'test', element: <TestPage /> },
  { path: 'login', element: <LoginPage /> },

  // Fallbacks
  { path: 'not-found', element: <NotFoundPage /> },
  { path: '404', element: <NotFoundPage /> },
  // keep this LAST
  { path: '*', element: <NotFoundPage /> }
];
